<template>
  <MatchMedia v-slot="{ matches: isDesktop }" device="desktop">
    <div
      :aria-disabled="!isAvailable"
      :class="[
        'product-to-invest__content',
        {
          'product-to-invest__content-grid': isDesktop,
          'product-to-invest__content-responsive': !isDesktop
        }
      ]"
    >
      <div class="product-to-invest__info-name">
        <BaseText
          :size="isDesktop ? 'md' : 'xl'"
          class="product-to-invest__title"
        >
          {{ productName }}
        </BaseText>
        <BaseText size="sm" class="product-to-invest__error-message">
          {{ errorMessage }}
        </BaseText>
      </div>
      <div
        v-if="isAvailable"
        :class="{ 'product-to-invest__content-min-invest': !isDesktop }"
      >
        <BaseText
          v-if="!isDesktop"
          size="sm"
          class="product-to-invest__text-min-invest"
        >
          Investimento mínimo
        </BaseText>
        <BaseText size="sm" class="product-to-invest__text-min-invest">
          {{ minToInvest.formatted }}
        </BaseText>
      </div>

      <InputMoney
        v-if="isAvailable"
        class="product-to-invest__content-input"
        placeholder="R$ 0,00"
        :value="inputValue"
        @input="handleInput"
        @blur="handleBlur"
      />
    </div>
  </MatchMedia>
</template>

<script lang="ts">
import { Component, Emit, Prop, Vue } from 'vue-property-decorator';
import { Row, BaseText, InputMoney } from '@warrenbrasil/nebraska-web';
import { MatchMedia } from '@/modules/common/components/MatchMedia';
import { getInvestmentAmount, getProductInvestmentAmount } from '@/modules/transaction/services/product-investment';
import { FeatureFlagsModule } from '@/modules/common/store/FeatureFlagsModule';
import { FeatureFlags } from '@/types/models/FeatureFlags';

@Component({
  components: { Row, BaseText, MatchMedia, InputMoney }
})
export default class ProductToInvestRow extends Vue {
  @Prop({ type: String, required: false })
  readonly errorMessage?: string;

  @Prop({ type: Boolean, required: false, default: true })
  readonly isAvailable?: boolean;

  @Prop({ type: Object, required: true })
  readonly minToInvest!: { value: number; formatted: string };

  @Prop({ type: String, required: true })
  readonly productName!: string;

  @Prop({ type: String, required: true })
  readonly productId!: string;

  public valueToInvest = 0;

  public get inputValue() {
    return this.valueToInvest > 0 ? this.valueToInvest : undefined;
  }

  get isEnabledProductBffUsaged() {
    return FeatureFlagsModule.isEnabled(FeatureFlags.ENABLE_BFF_INSTRUMENTS_MIGRATION);
  }

  handleInput(value: number) {
    this.updateValue(value);
  }

  @Emit()
  updateValue(value: number, fetchingAmount = true) {
    this.valueToInvest = value;

    return { id: this.productId, value: this.valueToInvest, fetchingAmount };
  }

  handleBlur() {
    if (this.valueToInvest >= this.minToInvest.value) {
      this.fetchInvestmentAmount(this.valueToInvest);
    }
  }

  async fetchInvestmentAmount(value: number) {
    try {
      const data = this.isEnabledProductBffUsaged
        ? await getProductInvestmentAmount(this.productId, value)
        : await getInvestmentAmount(this.productId, value);

      this.valueToInvest = data.amount.value;
      this.updateValue(data.amount.value, false);
    } catch {
      this.updateValue(0, false);
    }
  }
}
</script>
<style lang="less" scoped>
.product-to-invest {
  &__content {
    padding: @size-spacing-x500 0;
    border-bottom: 1px solid @divider-primary;
    &[aria-disabled^='true'] {
      .product-to-invest__title,
      .product-to-invest__text-min-invest {
        color: @text-disabled !important;
      }
    }
    > div {
      padding-left: @size-spacing-x400;
      .from-md({display: flex;});

      &:not(.product-to-invest__info-name) {
        align-items: center;
      }
    }

    /deep/ .wrn-input {
      margin-top: 0px;
    }
  }
  &__title {
    .to-md({font-weight: bold;});
  }
  &__content-grid {
    display: grid;
    grid-template-columns: 6fr 3fr 2fr;
  }
  &__content-responsive {
    padding: @size-spacing-x500 0;
    > div {
      padding-left: 0;
    }
  }
  &__info-name {
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;
  }
  &__error-message {
    color: @text-on-error !important;
  }
  &__content-input {
    padding-right: @size-spacing-x400;
    .to-md({padding-right: 0; padding-top: @size-spacing-x350});
  }
  &__content-min-invest {
    margin-top: @size-spacing-x150;
    display: flex;
    justify-content: space-between;
  }
}
</style>
